import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Howl } from 'howler';
import { FaGuitar, FaVolumeUp } from 'react-icons/fa';

const Guitar: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentString, setCurrentString] = useState<number | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const guitarRef = useRef<HTMLDivElement>(null);
  const soundRefs = useRef<Howl[]>([]);

  // Initialize guitar string sounds
  useEffect(() => {
    // Guitar string sounds (from low E to high E)
    const stringNotes = [
      '/sounds/guitar-e2.mp3',
      '/sounds/guitar-a2.mp3',
      '/sounds/guitar-d3.mp3',
      '/sounds/guitar-g3.mp3',
      '/sounds/guitar-b3.mp3',
      '/sounds/guitar-e4.mp3',
    ];

    // Create Howl instances for each string
    soundRefs.current = stringNotes.map(note => 
      new Howl({
        src: [note],
        volume: 0.7,
        onend: () => {
          setCurrentString(null);
        }
      })
    );

    return () => {
      // Stop all sounds when component unmounts
      soundRefs.current.forEach(sound => sound.stop());
    };
  }, []);

  // Play a guitar string sound
  const playString = (stringIndex: number) => {
    if (stringIndex >= 0 && stringIndex < soundRefs.current.length) {
      // Stop any currently playing sounds
      soundRefs.current.forEach(sound => sound.stop());
      
      // Play the selected string sound
      soundRefs.current[stringIndex].play();
      setCurrentString(stringIndex);
    }
  };

  // Handle mouse movement over the guitar
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!guitarRef.current || !isHovering) return;
    
    const rect = guitarRef.current.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const height = rect.height;
    
    // Determine which string is being hovered based on Y position
    const stringIndex = Math.floor((relativeY / height) * 6);
    
    // Only play if it's a different string than the current one
    if (stringIndex !== currentString && stringIndex >= 0 && stringIndex < 6) {
      playString(stringIndex);
    }
  };

  return (
    <section className="py-24 px-6 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent">
              Experience the Spanish Guitar
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Hover over the guitar to play different strings and immerse yourself in the sound of Spanish music
          </p>
        </motion.div>

        <div className="flex flex-col items-center justify-center">
          {/* Guitar Visualization - MUCH LARGER */}
          <motion.div 
            className="relative mb-8"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div 
              ref={guitarRef}
              className="relative w-[500px] h-[600px] cursor-pointer mx-auto"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
                setCurrentString(null);
                soundRefs.current.forEach(sound => sound.stop());
              }}
              onMouseMove={handleMouseMove}
            >
              {/* Guitar Body */}
              <div className="absolute w-[400px] h-[400px] bg-gradient-to-br from-amber-800 to-amber-950 rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-2xl">
                <div className="absolute w-[150px] h-[150px] bg-black rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
              </div>
              
              {/* Guitar Neck */}
              <div className="absolute w-[80px] h-[500px] bg-gradient-to-b from-amber-700 to-amber-900 top-0 left-1/2 transform -translate-x-1/2 rounded-t-lg">
                {/* Frets */}
                {[...Array(8)].map((_, i) => (
                  <div 
                    key={i} 
                    className="absolute w-full h-1 bg-gray-300" 
                    style={{ top: `${10 + i * 10}%` }}
                  ></div>
                ))}
                
                {/* Fret Markers */}
                <div className="absolute w-6 h-6 bg-gray-300/30 rounded-full top-[25%] left-1/2 transform -translate-x-1/2"></div>
                <div className="absolute w-6 h-6 bg-gray-300/30 rounded-full top-[45%] left-1/2 transform -translate-x-1/2"></div>
                <div className="absolute w-6 h-6 bg-gray-300/30 rounded-full top-[65%] left-1/2 transform -translate-x-1/2"></div>
              </div>
              
              {/* Guitar Strings */}
              {[...Array(6)].map((_, i) => (
                <div 
                  key={i}
                  className={`absolute h-full bg-gradient-to-b ${
                    currentString === i 
                      ? 'from-yellow-300 via-white to-yellow-300 animate-pulse' 
                      : 'from-gray-300 to-gray-400'
                  } left-1/2 transform -translate-x-1/2`}
                  style={{ 
                    left: `${42 + i * 3}%`,
                    width: `${1 + (5-i) * 0.5}px`, // Thicker strings at the bottom
                    filter: currentString === i ? 'drop-shadow(0 0 5px rgba(234, 179, 8, 0.8))' : 'none'
                  }}
                ></div>
              ))}
              
              {/* String Vibration Effect */}
              {currentString !== null && (
                <motion.div
                  className="absolute h-full bg-yellow-400/50 left-1/2 transform -translate-x-1/2 z-10"
                  style={{ 
                    left: `${42 + currentString * 3}%`,
                    width: '4px',
                    filter: 'blur(2px)'
                  }}
                  initial={{ scaleY: 1, opacity: 0.8 }}
                  animate={{ 
                    scaleY: [1, 1.02, 0.98, 1.01, 0.99, 1],
                    opacity: [0.8, 0.6, 0.4, 0.2, 0]
                  }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                ></motion.div>
              )}
              
              {/* Sound Indicator */}
              {currentString !== null && (
                <motion.div
                  className="absolute top-8 right-8 bg-yellow-500/20 rounded-full p-3"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                >
                  <FaVolumeUp className="text-yellow-400 w-6 h-6" />
                </motion.div>
              )}
            </div>
            
            {/* Hover Instruction */}
            <motion.p 
              className="text-center text-gray-400 mt-4 text-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              Hover over the strings to play
            </motion.p>
          </motion.div>
          
          {/* Guitar Information */}
          <motion.div 
            className="max-w-2xl text-center"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-3xl font-bold mb-6">The Heart of Spanish Music</h3>
            <p className="text-gray-300 text-xl mb-8">
              The Spanish guitar is central to many Latin music genres, from flamenco to mariachi. 
              Learning Spanish through music helps you connect with the culture and improves your 
              pronunciation and rhythm.
            </p>
            
            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700 max-w-2xl mx-auto">
              <h4 className="font-bold text-xl mb-4 flex items-center justify-center">
                <FaGuitar className="text-yellow-400 mr-2 text-2xl" />
                Spanish Guitar Vocabulary
              </h4>
              <div className="grid grid-cols-2 gap-6 text-lg">
                <div className="flex justify-between">
                  <span className="text-yellow-400">La guitarra</span>
                  <span className="text-gray-400">The guitar</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-400">Las cuerdas</span>
                  <span className="text-gray-400">The strings</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-400">Tocar la guitarra</span>
                  <span className="text-gray-400">To play the guitar</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-400">El ritmo</span>
                  <span className="text-gray-400">The rhythm</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Musical Notes Background */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-yellow-500/5 text-6xl md:text-8xl"
            initial={{ 
              x: `${Math.random() * 100}%`, 
              y: `${Math.random() * 100}%`,
              rotate: Math.random() * 360
            }}
            animate={{ 
              y: [null, `${Math.random() * 100}%`],
              rotate: [null, Math.random() * 360 + 180]
            }}
            transition={{ 
              duration: Math.random() * 20 + 20, 
              repeat: Infinity,
              ease: "linear"
            }}
          >
            {['♪', '♫', '♬', '♩', '♭', '♮', '♯'][Math.floor(Math.random() * 7)]}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Guitar;
